import type {Moment} from 'moment-timezone'
import moment from 'moment-timezone'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import type {ProductT} from '@freckle/educator-entities/ts/common/helpers/products'
import {useChromeless} from './chromeless/hook'

/** Deep link activity types for Renaissance integration */
export type DeepLinkActivityT =
  | 'freckle_math_targeted_assignment'
  | 'freckle_ela_targeted_assignment'
  | 'freckle_word_study_targeted_assignment'
  | 'freckle_math_dok_assignment'
  | 'freckle_math_ibl_assignment'

type PostMessageCreatedAssignmentT = {
  unifiedAssignmentId: string | null
  classId: string
  studentIds: string[]
  title: string | null
  appCode: 'APPS_FR'
  deepLink: {
    activity: DeepLinkActivityT
    freckleAssignmentId: number
  }
  externalId: string
  startDate: Moment | null
  // We don't support this, spec says to pass `null`:
  expirationDate: null
  // We don't support this, spec says to pass `null`:
  dueDate: null
  // Omitted because we don't support it:
  // highPriority: ...
  // Omitted because we don't support it:
  // type: ...
}

type CreateSuccess = {
  source: 'renaissance_next_apps_fr+assignment_created'
  status: 'success'
  assignment: PostMessageCreatedAssignmentT
}

export type LoadFailure = {
  source: 'renaissance_next_apps_fr+error'
  status: 'error'
  reason?: string
}

export type CreateFailure = {
  source: 'renaissance_next_apps_fr+assignment_created'
  status: 'failure'
}

export type Sizing = {
  source: `renaissance_next_apps_fr+sizing`
  height: string
  width: string
}

export type Message =
  | {source: 'renaissance_next_apps_fr+initialization_complete'; status: 'initialized'}
  | CreateSuccess
  | CreateFailure
  | LoadFailure
  | Sizing

export const mkFailureToLoad = (reason?: string): LoadFailure => ({
  source: 'renaissance_next_apps_fr+error',
  status: 'error',
  ...(reason !== undefined ? {reason} : {})
})

export const mkFailureToCreate = (): CreateFailure => ({
  source: 'renaissance_next_apps_fr+assignment_created',
  status: 'failure'
})

export const mkSuccess = ({
  assignment
}: {
  product: ProductT
  assignment: Omit<PostMessageCreatedAssignmentT, 'expirationDate' | 'dueDate' | 'appCode'>
}): CreateSuccess => ({
  source: 'renaissance_next_apps_fr+assignment_created',
  status: 'success',
  assignment: {...assignment, appCode: 'APPS_FR', expirationDate: null, dueDate: null}
})

export const useChromelessPostMessage = (): ((opts: {
  message: Message
  chromeAlternative?: () => void
}) => void) => {
  const chromeless = useChromeless()

  return chromeless
    ? opts => postMessageToWildcard(opts)
    : ({chromeAlternative}) => {
        if (chromeAlternative !== undefined) chromeAlternative()
      }
}

export const useChromelessPostAssignmentPageLoaded = (product: ProductT) => {
  const chromelessPostMessage = useChromelessPostMessage()

  useSafeEffectExtraDeps<{chromelessPostMessage: typeof chromelessPostMessage}>(
    ({chromelessPostMessage}) => {
      chromelessPostMessage({
        message: {
          source: 'renaissance_next_apps_fr+initialization_complete',
          status: 'initialized'
        }
      })
    },
    [product],
    {chromelessPostMessage: staticExtra(chromelessPostMessage)}
  )
}

// Renaissance Next sizing contract caps width at 1120px.
// https://illuminate.atlassian.net/wiki/spaces/TEACH/pages/18612224518/Next+2.0+iframe+Knowledgebase#sizing
const RENAISSANCE_NEXT_MAX_WIDTH = 1120

/** While chromeless, observes layout changes and posts the current content height and capped width to the parent frame. */
export const useChromelessPostSizing = () => {
  const chromelessPostMessage = useChromelessPostMessage()
  useSafeEffectExtraDeps<{chromelessPostMessage: typeof chromelessPostMessage}>(
    ({chromelessPostMessage}) => {
      let frameId: number | null = null
      let lastHeight = -1
      let lastWidth = -1
      let maxWidth = -1
      const post = () => {
        frameId = null
        const height = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)
        const width = Math.min(
          Math.max(document.documentElement.scrollWidth, document.body.scrollWidth),
          RENAISSANCE_NEXT_MAX_WIDTH
        )
        if (height === lastHeight && width === lastWidth) return
        lastHeight = height
        lastWidth = width
        maxWidth = Math.max(maxWidth, width)
        chromelessPostMessage({
          message: {
            source: 'renaissance_next_apps_fr+sizing',
            height: String(height),
            width: String(maxWidth)
          }
        })
      }
      const schedulePost = () => {
        if (frameId !== null) return
        frameId = window.requestAnimationFrame(post)
      }
      const resizeObserver = new ResizeObserver(schedulePost)
      resizeObserver.observe(document.body)
      resizeObserver.observe(document.documentElement)
      const mutationObserver = new MutationObserver(schedulePost)
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden']
      })
      schedulePost()
      return () => {
        resizeObserver.disconnect()
        mutationObserver.disconnect()
        if (frameId !== null) window.cancelAnimationFrame(frameId)
      }
    },
    [],
    {chromelessPostMessage: staticExtra(chromelessPostMessage)}
  )
}

export const postMessageToWildcard = ({message}: {message: Message}) => {
  const target = WILDCARD_TARGET
  const v2 = serializeMoments(message)
  console.log('Sent message with postMessage', {message: v2, target})
  window.parent.postMessage(v2, target)
}

export const resolveRenaissanceStudentIds = (
  freckleStudentIds: number[],
  students: Array<{id: number; renaissanceRPIdentifier: string | undefined | null}>
): string[] =>
  freckleStudentIds.flatMap(freckleStudentId => {
    const student = students.find(s => s.id === freckleStudentId)
    const renaissanceId = student?.renaissanceRPIdentifier
    return renaissanceId !== null && renaissanceId !== undefined && renaissanceId !== ''
      ? [renaissanceId]
      : []
  })

export const resolveRenaissanceCourseId = (
  freckleCourseId: number,
  courses: Array<{id: number; renaissanceRPIdentifier?: string | undefined | null}>
): string | null => {
  const course = courses.find(c => c.id === freckleCourseId)
  const renaissanceId = course?.renaissanceRPIdentifier
  return renaissanceId !== null && renaissanceId !== undefined && renaissanceId !== ''
    ? renaissanceId
    : null
}

// See https://illuminate.atlassian.net/wiki/spaces/TEACH/pages/18612224518/Next+2.0+iframe+Knowledgebase#Securely-Allow-List-postMessage()-Domains
const WILDCARD_TARGET = '*'

const serializeMoments = (value: unknown): unknown =>
  JSON.parse(JSON.stringify(value, (_, v) => (moment.isMoment(v) ? v.toISOString() : v)))
